import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { GraphNode, GraphLink } from './types';
import { SYMBOLS, getCommsTier } from './utils/graphUtils';

interface D3HybridViewerProps {
    nodes: GraphNode[];
    links: GraphLink[];
    onNodeClick?: (node: GraphNode) => void;
    orientation: 'TD' | 'LR';
    expandedIds: Set<string>;
    onToggleExpand: (nodeId: string) => void;
    powerVisMode: 'none' | 'shadow' | 'halo' | 'bar';
    selectedNodeIds: Set<string>;
    onBackgroundClick?: () => void;
    visibleNodeIds: Set<string>;
    highlightedPath: Set<string>;
}

// Extended node type for simulation
interface SimNode extends GraphNode {
    x: number;
    y: number;
    vx: number;
    vy: number;
    fx: number | null;
    fy: number | null;
    targetCross: number;
    effectiveTier: number;
}

// Extended link type for simulation
interface SimLink {
    source: SimNode | string;
    target: SimNode | string;
    color: string;
    details: { signal?: string };
}

const D3HybridViewer: React.FC<D3HybridViewerProps> = ({
    nodes,
    links,
    onNodeClick,
    orientation,
    expandedIds,
    onToggleExpand,
    powerVisMode,
    selectedNodeIds,
    onBackgroundClick,
    visibleNodeIds,
    highlightedPath
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 800, height: 600 });

    const transformRef = useRef<d3.ZoomTransform>(d3.zoomIdentity);
    const nodeStateRef = useRef<Map<string, { x: number, y: number, vx: number, vy: number }>>(new Map());
    const simulationRef = useRef<d3.Simulation<SimNode, SimLink> | null>(null);
    const d3NodesRef = useRef<SimNode[]>([]);
    const d3LinksRef = useRef<SimLink[]>([]);
    const nodeMapRef = useRef<Map<string, SimNode>>(new Map());
    const dragNodeRef = useRef<SimNode | null>(null);
    const isDraggingRef = useRef(false);
    const animationFrameRef = useRef<number | null>(null);
    const drawFnRef = useRef<(() => void) | null>(null);
    const zoomBehaviorRef = useRef<d3.ZoomBehavior<HTMLCanvasElement, unknown> | null>(null);
    const needsRedrawRef = useRef(false);

    // Precomputed per-node flags (computed once when topology changes, not per frame)
    const nodeFlagsRef = useRef<Map<string, {
        hasChildren: boolean;
        hasHiddenChildren: boolean;
        hasUnrenderedCommLinks: boolean;
    }>>(new Map());

    // Cached Path2D shapes per symbol type
    const symbolCacheRef = useRef<Map<string, Path2D>>(new Map());

    // Resize observer
    useEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver(entries => {
            for (const entry of entries) {
                setDimensions({
                    width: entry.contentRect.width,
                    height: entry.contentRect.height
                });
            }
        });
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, []);

    // Helper: Plasma Color Scale for power visualization
    const getPlasmaColor = useCallback((t: number): string => {
        if (t < 0.2) return d3.interpolateRgb("#0d0887", "#5402a3")(t / 0.2);
        if (t < 0.4) return d3.interpolateRgb("#5402a3", "#900da3")(t / 0.2 - 1);
        if (t < 0.6) return d3.interpolateRgb("#900da3", "#cb4778")(t / 0.2 - 2);
        if (t < 0.8) return d3.interpolateRgb("#cb4778", "#f0894f")(t / 0.2 - 3);
        return d3.interpolateRgb("#f0894f", "#fcce25")(t / 0.2 - 4);
    }, []);

    // Helper: Get cached Path2D for a symbol type (parsed once, reused every frame)
    const getSymbolPath2D = useCallback((type: string): Path2D => {
        const cache = symbolCacheRef.current;
        let cached = cache.get(type);
        if (cached) return cached;

        // Parse SVG path string and build Path2D once
        const pathStr = (() => {
            switch (type) {
                // Grid & Utility
                case 'grid_connection_point': return SYMBOLS.grid;
                case 'grid': return SYMBOLS.grid;

                // Transformers
                case 'transformer': return SYMBOLS.transformer;
                case 'control_transformer': return SYMBOLS.control_transformer;
                case 'current_transformer': return SYMBOLS.current_transformer;
                case 'voltage_transformer': return SYMBOLS.voltage_transformer;

                // Power Conversion
                case 'inverter': return SYMBOLS.inverter;
                case 'pcs_bidirectional': return SYMBOLS.pcs_bidirectional;
                case 'mppt': return SYMBOLS.mppt;
                case 'dc_optimizer': return SYMBOLS.dc_optimizer;
                case 'microinverter': return SYMBOLS.microinverter;

                // Protection Devices
                case 'circuit_breaker': return SYMBOLS.breaker;
                case 'fuse': return SYMBOLS.fuse;
                case 'disconnect_switch': return SYMBOLS.switch;
                case 'protective_relay': return SYMBOLS.protective_relay;
                case 'surge_protection_device': return SYMBOLS.surge_protection_device;
                case 'rapid_shutdown_device': return SYMBOLS.rapid_shutdown_device;

                // Distribution Equipment
                case 'pv_module': return SYMBOLS.module;
                case 'string': return SYMBOLS.string;
                case 'combiner_box': return SYMBOLS.combiner_box;
                case 'junction_box': return SYMBOLS.junction_box;
                case 'panelboard': return SYMBOLS.panelboard;
                case 'switchboard_lv': return SYMBOLS.switchboard_lv;
                case 'switchgear_mv': return SYMBOLS.switchgear_mv;

                // Metering
                case 'revenue_meter': return SYMBOLS.revenue_meter;

                // Monitoring & Weather
                case 'gateway': return SYMBOLS.gateway;
                case 'weather_station': return SYMBOLS.weather_station;
                case 'pyranometer': return SYMBOLS.pyranometer;
                case 'ambient_temp_sensor': return SYMBOLS.ambient_temp_sensor;
                case 'module_temp_sensor': return SYMBOLS.module_temp_sensor;
                case 'anemometer': return SYMBOLS.anemometer;
                case 'rain_gauge': return SYMBOLS.rain_gauge;

                // Infrastructure
                case 'grounding_system': return SYMBOLS.grounding_system;
                case 'racking_system': return SYMBOLS.racking_system;
                case 'tracker_controller': return SYMBOLS.tracker_controller;

                default: return SYMBOLS.default;
            }
        })();

        const path = new Path2D();
        const commands = pathStr.match(/[MLQTCSAZ][^MLQTCSAZ]*/gi) || [];

        commands.forEach(cmd => {
            const t = cmd[0].toUpperCase();
            const args = cmd.slice(1).trim().split(/[\s,]+/).map(parseFloat).filter(n => !isNaN(n));

            switch (t) {
                case 'M': path.moveTo(args[0], args[1]); break;
                case 'L': path.lineTo(args[0], args[1]); break;
                case 'Q': path.quadraticCurveTo(args[0], args[1], args[2], args[3]); break;
                case 'T': path.lineTo(args[0], args[1]); break;
                case 'C': path.bezierCurveTo(args[0], args[1], args[2], args[3], args[4], args[5]); break;
                case 'A':
                    if (args.length >= 7) {
                        path.arc(args[5], args[6], args[0], 0, Math.PI * 2);
                    }
                    break;
                case 'Z': path.closePath(); break;
            }
        });

        cache.set(type, path);
        return path;
    }, []);

    // Draw a single node
    const drawNode = useCallback((
        ctx: CanvasRenderingContext2D,
        node: SimNode,
        isSelected: boolean,
        isInPath: boolean,
        isHighlighting: boolean,
        hasChildren: boolean,
        isExpanded: boolean,
        hasHiddenChildren: boolean,
        hasUnrenderedCommLinks: boolean
    ) => {
        ctx.save();
        ctx.translate(node.x, node.y);

        // Calculate opacity based on selection/highlighting state
        // Non-highlighted/non-selected items are heavily faded for visual focus
        let opacity = 1;
        if (isHighlighting) {
            opacity = isInPath ? 1 : 0.5;  // 90% transparent for non-path items
        } else if (selectedNodeIds.size > 0) {
            opacity = isSelected ? 1 : 0.55;  // 85% transparent for non-selected items
        }

        // Power visualization - halo
        if (powerVisMode === 'halo' && node.normalizedPower !== undefined && node.cumulativePower && node.cumulativePower > 0) {
            const color = getPlasmaColor(node.normalizedPower);
            const radius = 12 + (node.normalizedPower * 8);
            ctx.beginPath();
            ctx.arc(0, 0, radius, 0, Math.PI * 2);
            ctx.strokeStyle = color;
            ctx.lineWidth = 2 + (node.normalizedPower * 3);
            ctx.globalAlpha = 0.8 * opacity;
            ctx.stroke();
        }

        // Power visualization - bar
        if (powerVisMode === 'bar' && node.normalizedPower !== undefined && node.cumulativePower && node.cumulativePower > 0) {
            const color = getPlasmaColor(node.normalizedPower);
            const barHeight = 10 + (node.normalizedPower * 20);
            ctx.fillStyle = color;
            ctx.globalAlpha = opacity;
            ctx.beginPath();
            ctx.roundRect(14, -barHeight / 2, 4, barHeight, 2);
            ctx.fill();
        }

        // Draw node shape (uses cached Path2D)
        const path = getSymbolPath2D(node.type);

        // Fill with darker color
        const baseColor = d3.color(node.color);
        ctx.fillStyle = baseColor?.darker(0.5).toString() || "#000";
        ctx.globalAlpha = opacity;
        ctx.fill(path);

        // Stroke
        ctx.strokeStyle = isSelected ? "#10b981" : node.color;
        ctx.lineWidth = isSelected ? 3 : 2;

        // Power shadow effect
        if (powerVisMode === 'shadow' && node.normalizedPower !== undefined && node.cumulativePower && node.cumulativePower > 0) {
            const shadowColor = getPlasmaColor(node.normalizedPower);
            const blur = 3 + (node.normalizedPower * 5);
            ctx.shadowColor = shadowColor;
            ctx.shadowBlur = blur;
        }

        ctx.stroke(path);
        ctx.shadowBlur = 0;

        // Draw label
        if (node.type !== 'pv_module') {
            ctx.fillStyle = "#e5e7eb";
            ctx.globalAlpha = opacity;
            ctx.font = "10px monospace";
            ctx.textAlign = "center";
            ctx.textBaseline = "top";
            ctx.fillText(node.id, 0, 22);
        }

        // Expand/Collapse toggle
        if (hasChildren) {
            ctx.save();
            ctx.translate(15, -15);

            // Toggle circle background
            ctx.beginPath();
            ctx.arc(0, 0, 6, 0, Math.PI * 2);
            ctx.fillStyle = "#1f2937";
            ctx.fill();
            ctx.strokeStyle = isExpanded ? "#ef4444" : "#10b981";
            ctx.lineWidth = 1.5;
            ctx.stroke();

            // Toggle text
            ctx.fillStyle = "#fff";
            ctx.font = "bold 10px sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(isExpanded ? "-" : "+", 0, 0);

            ctx.restore();
        }

        // Stub for hidden hierarchical children
        if (hasHiddenChildren) {
            ctx.strokeStyle = "#9CA3AF";
            ctx.lineWidth = 2;
            ctx.setLineDash([2, 2]);
            ctx.beginPath();
            if (orientation === 'TD') {
                ctx.moveTo(0, 20);
                ctx.lineTo(0, 35);
            } else {
                ctx.moveTo(20, 0);
                ctx.lineTo(35, 0);
            }
            ctx.stroke();
            ctx.setLineDash([]);

            // Stub dot
            ctx.beginPath();
            ctx.arc(orientation === 'TD' ? 0 : 35, orientation === 'TD' ? 35 : 0, 2, 0, Math.PI * 2);
            ctx.fillStyle = "#9CA3AF";
            ctx.fill();
        }

        // Stub for unrendered comm links
        if (hasUnrenderedCommLinks) {
            ctx.strokeStyle = "#3B82F6";
            ctx.lineWidth = 2;
            ctx.setLineDash([4, 2]);
            ctx.beginPath();
            if (orientation === 'TD') {
                ctx.moveTo(-20, 0);
                ctx.lineTo(-35, 0);
            } else {
                ctx.moveTo(0, -20);
                ctx.lineTo(0, -35);
            }
            ctx.stroke();
            ctx.setLineDash([]);

            ctx.beginPath();
            ctx.arc(orientation === 'TD' ? -35 : 0, orientation === 'TD' ? 0 : -35, 2, 0, Math.PI * 2);
            ctx.fillStyle = "#3B82F6";
            ctx.fill();
        }

        ctx.restore();
    }, [orientation, powerVisMode, selectedNodeIds, getPlasmaColor, getSymbolPath2D]);

    // Draw a single link
    const drawLink = useCallback((ctx: CanvasRenderingContext2D, link: SimLink, nodeMap: Map<string, SimNode>) => {
        let s = typeof link.source === 'object' ? link.source : nodeMap.get(link.source as string);
        let t = typeof link.target === 'object' ? link.target : nodeMap.get(link.target as string);

        if (!s || !t) return;

        const x1 = s.x, y1 = s.y, x2 = t.x, y2 = t.y;
        const mainDiff = orientation === 'TD' ? Math.abs(y1 - y2) : Math.abs(x1 - x2);
        const isComm = link.details?.signal === 'COMM';

        ctx.beginPath();
        ctx.strokeStyle = link.color;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.6;

        if (isComm) {
            ctx.setLineDash([4, 2]);
        }

        if (mainDiff < 30) {
            // Arc for same-tier connections
            const dr = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2) * 0.5;
            const midX = (x1 + x2) / 2;
            const midY = (y1 + y2) / 2;
            const perpX = -(y2 - y1) / Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2 + 0.001);
            const perpY = (x2 - x1) / Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2 + 0.001);
            const ctrlX = midX + perpX * dr;
            const ctrlY = midY + perpY * dr;

            ctx.moveTo(x1, y1);
            ctx.quadraticCurveTo(ctrlX, ctrlY, x2, y2);
        } else {
            // Bezier curve for hierarchical connections
            if (orientation === 'TD') {
                const midY = (y1 + y2) / 2;
                ctx.moveTo(x1, y1);
                ctx.bezierCurveTo(x1, midY, x2, midY, x2, y2);
            } else {
                const midX = (x1 + x2) / 2;
                ctx.moveTo(x1, y1);
                ctx.bezierCurveTo(midX, y1, midX, y2, x2, y2);
            }
        }

        ctx.stroke();
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;
    }, [orientation]);

    // Main draw function
    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const dpr = window.devicePixelRatio || 1;
        const width = dimensions.width;
        const height = dimensions.height;

        // Clear canvas
        ctx.save();
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, width, height);

        // Apply zoom transform
        const t = transformRef.current;
        ctx.translate(t.x, t.y);
        ctx.scale(t.k, t.k);

        const nodeMap = nodeMapRef.current;
        const d3Nodes = d3NodesRef.current;
        const d3Links = d3LinksRef.current;

        // Draw links first (below nodes)
        d3Links.forEach(link => drawLink(ctx, link, nodeMap));

        // Draw nodes (use precomputed flags for performance)
        const isHighlighting = highlightedPath.size > 0;
        const flags = nodeFlagsRef.current;

        d3Nodes.forEach(node => {
            const isSelected = selectedNodeIds.has(node.id);
            const isInPath = highlightedPath.has(node.id);
            const isExpanded = expandedIds.has(node.id);

            // Use precomputed flags (O(1) lookup instead of O(links) per node)
            const nodeFlags = flags.get(node.id);
            const hasChildren = nodeFlags?.hasChildren ?? false;
            const hasHiddenChildren = nodeFlags?.hasHiddenChildren ?? false;
            const hasUnrenderedCommLinks = nodeFlags?.hasUnrenderedCommLinks ?? false;

            drawNode(ctx, node, isSelected, isInPath, isHighlighting, hasChildren, isExpanded, hasHiddenChildren, hasUnrenderedCommLinks);
        });

        ctx.restore();
    }, [dimensions, drawLink, drawNode, highlightedPath, selectedNodeIds, expandedIds]);

    // Keep draw function ref updated
    useEffect(() => {
        drawFnRef.current = draw;
    }, [draw]);

    // Precompute node flags when topology changes (not per frame!)
    useEffect(() => {
        const flags = new Map<string, {
            hasChildren: boolean;
            hasHiddenChildren: boolean;
            hasUnrenderedCommLinks: boolean;
        }>();

        // Build adjacency maps once
        const childrenMap = new Map<string, string[]>();
        const commAdjMap = new Map<string, string[]>();

        for (const link of links) {
            const source = String(link.source);
            const target = String(link.target);

            if (link.details?.signal === 'COMM') {
                // Comm links - bidirectional adjacency
                if (!commAdjMap.has(source)) commAdjMap.set(source, []);
                if (!commAdjMap.has(target)) commAdjMap.set(target, []);
                commAdjMap.get(source)!.push(target);
                commAdjMap.get(target)!.push(source);
            } else {
                // Hierarchical links - source is child of target
                if (!childrenMap.has(target)) childrenMap.set(target, []);
                childrenMap.get(target)!.push(source);
            }
        }

        // Compute flags for each node
        for (const node of nodes) {
            const children = childrenMap.get(node.id) ?? [];
            const hasChildren = children.length > 0;
            const hasHiddenChildren = hasChildren && children.some(c => !visibleNodeIds.has(c));

            const commNeighbors = commAdjMap.get(node.id) ?? [];
            const hasUnrenderedCommLinks = commNeighbors.some(otherId => !visibleNodeIds.has(otherId));

            flags.set(node.id, { hasChildren, hasHiddenChildren, hasUnrenderedCommLinks });
        }

        nodeFlagsRef.current = flags;
    }, [nodes, links, visibleNodeIds]);

    // Hit testing - find node at point
    const getNodeAtPoint = useCallback((clientX: number, clientY: number): SimNode | null => {
        const canvas = canvasRef.current;
        if (!canvas) return null;

        const rect = canvas.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;

        // Invert zoom transform
        const t = transformRef.current;
        const tx = (x - t.x) / t.k;
        const ty = (y - t.y) / t.k;

        // Find node within radius (reverse order for top-most)
        const d3Nodes = d3NodesRef.current;
        for (let i = d3Nodes.length - 1; i >= 0; i--) {
            const node = d3Nodes[i];
            const dx = tx - node.x;
            const dy = ty - node.y;
            if (dx * dx + dy * dy < 400) { // 20px radius squared
                return node;
            }
        }
        return null;
    }, []);

    // Check if click is on toggle button
    const isToggleClick = useCallback((clientX: number, clientY: number, node: SimNode): boolean => {
        const canvas = canvasRef.current;
        if (!canvas) return false;

        const rect = canvas.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;

        const t = transformRef.current;
        const tx = (x - t.x) / t.k;
        const ty = (y - t.y) / t.k;

        // Toggle button is at (node.x + 15, node.y - 15)
        const btnX = node.x + 15;
        const btnY = node.y - 15;
        const dx = tx - btnX;
        const dy = ty - btnY;

        return dx * dx + dy * dy < 64; // 8px radius
    }, []);

    // --- EFFECT 1: Topology & Simulation Setup ---
    useEffect(() => {
        if (!canvasRef.current || dimensions.width === 0) return;

        const width = dimensions.width;
        const height = dimensions.height;

        // Setup canvas with device pixel ratio
        const canvas = canvasRef.current;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        // Use passed Visible Nodes
        const visibleNodes = nodes.filter(n => visibleNodeIds.has(n.id));
        const visibleLinks = links.filter(l =>
            visibleNodeIds.has(l.source) && visibleNodeIds.has(l.target)
        );

        // Separate main power hierarchy nodes from ancillary/comms nodes
        const mainNodes = visibleNodes.filter(n => !n.isAncillary);
        const ancillaryNodes = visibleNodes.filter(n => n.isAncillary);

        // Group main nodes by power tier
        const nodesByTier = new Map<number, GraphNode[]>();
        mainNodes.forEach(n => {
            if (!nodesByTier.has(n.tier)) nodesByTier.set(n.tier, []);
            nodesByTier.get(n.tier)?.push(n);
        });

        // Group ancillary nodes by comms tier
        const nodesByCommsTier = new Map<number, GraphNode[]>();
        ancillaryNodes.forEach(n => {
            const commsTier = getCommsTier(n.type);
            if (!nodesByCommsTier.has(commsTier)) nodesByCommsTier.set(commsTier, []);
            nodesByCommsTier.get(commsTier)?.push(n);
        });

        const nodeTargetCross = new Map<string, number>();
        const nodeTargetTier = new Map<string, number>();
        const crossDimension = orientation === 'TD' ? width : height;
        const tierNumbers = Array.from(nodesByTier.keys()).sort((a, b) => a - b);
        const commsTierNumbers = Array.from(nodesByCommsTier.keys()).sort((a, b) => a - b);

        // Canvas layout: Main hierarchy gets 70% width, Comms gets 25% on the right
        const mainSectionWidth = crossDimension * 0.65;
        const commsSectionStart = crossDimension * 0.75;
        const commsSectionWidth = crossDimension * 0.2;

        const getNumericSuffix = (id: string): number => {
            const match = id.match(/(\d+)$/);
            return match ? parseInt(match[1], 10) : 0;
        };

        // Position main power hierarchy nodes (centered in left 70%)
        const assignMainPositions = (nodeIds: string[]) => {
            if (nodeIds.length === 0) return;
            const count = nodeIds.length;
            const spread = Math.min(mainSectionWidth * 1.5, Math.max(250, count * 130));
            const start = (mainSectionWidth - spread) / 2;
            const step = spread / (count > 1 ? count - 1 : 1);
            nodeIds.forEach((id, idx) => {
                nodeTargetCross.set(id, count === 1 ? mainSectionWidth / 2 : start + step * idx);
            });
        };

        // Position ancillary nodes in comms section (right side)
        const assignCommsPositions = (nodeIds: string[]) => {
            if (nodeIds.length === 0) return;
            const count = nodeIds.length;
            const spread = Math.min(commsSectionWidth, count * 60);
            const start = commsSectionStart + (commsSectionWidth - spread) / 2;
            const step = spread / (count > 1 ? count - 1 : 1);
            nodeIds.forEach((id, idx) => {
                nodeTargetCross.set(id, count === 1 ? commsSectionStart + commsSectionWidth / 2 : start + step * idx);
            });
        };

        // Position main power hierarchy nodes
        tierNumbers.forEach(tier => {
            const tierNodes = nodesByTier.get(tier) || [];
            tierNodes.sort((a, b) => {
                const numA = getNumericSuffix(a.id);
                const numB = getNumericSuffix(b.id);
                if (numA !== numB) return numA - numB;
                return a.id.localeCompare(b.id, undefined, { numeric: true });
            });
            assignMainPositions(tierNodes.map(n => n.id));
        });

        // Position ancillary nodes by comms tier
        commsTierNumbers.forEach(commsTier => {
            const tierNodes = nodesByCommsTier.get(commsTier) || [];
            tierNodes.sort((a, b) => {
                const numA = getNumericSuffix(a.id);
                const numB = getNumericSuffix(b.id);
                if (numA !== numB) return numA - numB;
                return a.id.localeCompare(b.id, undefined, { numeric: true });
            });
            assignCommsPositions(tierNodes.map(n => n.id));
            // Use comms tier for vertical positioning (offset to align with power tiers visually)
            tierNodes.forEach(n => nodeTargetTier.set(n.id, commsTier));
        });

        // Variable tier spacing for power hierarchy
        const getTierPosition = (tier: number, isAncillary = false): number => {
            const baseSpacing = 150;
            const moduleSpacing = 50;
            const moduleStartTier = 10;

            if (isAncillary) {
                // Comms tiers 1-9 map to positions with consistent spacing
                const commsBaseSpacing = 100;
                return tier * commsBaseSpacing;
            }

            if (tier <= moduleStartTier) {
                return tier * baseSpacing;
            } else {
                const normalPart = moduleStartTier * baseSpacing;
                const modulePart = (tier - moduleStartTier) * moduleSpacing;
                return normalPart + modulePart;
            }
        };

        // Initialize D3 Nodes
        const d3Nodes: SimNode[] = visibleNodes.map(n => {
            const prev = nodeStateRef.current.get(n.id);
            const targetCross = nodeTargetCross.get(n.id) || (orientation === 'TD' ? width / 2 : height / 2);
            const effectiveTier = nodeTargetTier.get(n.id) ?? n.tier;
            const tierPos = getTierPosition(effectiveTier, n.isAncillary);
            return {
                ...n,
                x: prev ? prev.x : (orientation === 'TD' ? targetCross : tierPos),
                y: prev ? prev.y : (orientation === 'TD' ? tierPos : targetCross),
                vx: prev ? prev.vx : 0,
                vy: prev ? prev.vy : 0,
                fx: null,
                fy: null,
                targetCross: targetCross,
                effectiveTier: effectiveTier
            };
        });

        const nodeMap = new Map(d3Nodes.map(n => [n.id, n]));

        // Initialize Links
        const d3Links: SimLink[] = visibleLinks.map(l => ({ ...l }));
        const simLinks = d3Links.filter(l =>
            l.details?.signal !== 'COMM' &&
            nodeMap.has(l.source as string) && nodeMap.has(l.target as string)
        );

        // Store refs
        d3NodesRef.current = d3Nodes;
        d3LinksRef.current = d3Links;
        nodeMapRef.current = nodeMap;

        // Stop existing simulation
        if (simulationRef.current) simulationRef.current.stop();

        // Create simulation
        const simulation = d3.forceSimulation(d3Nodes)
            .force("link", d3.forceLink(simLinks)
                .id((d: any) => d.id)
                .distance(50)
                .strength(0.5)
            )
            .force("charge", d3.forceManyBody().strength(-200))
            .force("collide", d3.forceCollide().radius(30).strength(0.8))
            .velocityDecay(0.6)
            .alpha(0.6)
            .alphaDecay(0.05);

        // Custom strength function - ancillary nodes have stronger cross-axis pull to stay in their section
        const crossStrength = (d: any) => d.isAncillary ? 0.5 : 0.2;

        if (orientation === 'TD') {
            simulation
                .force("y", d3.forceY((d: any) => getTierPosition(d.effectiveTier, d.isAncillary)).strength(3.0))
                .force("x", d3.forceX((d: any) => d.targetCross).strength(crossStrength));
        } else {
            simulation
                .force("x", d3.forceX((d: any) => getTierPosition(d.effectiveTier, d.isAncillary)).strength(3.0))
                .force("y", d3.forceY((d: any) => d.targetCross).strength(crossStrength));
        }

        simulationRef.current = simulation;

        // Initial transform
        if (!transformRef.current || transformRef.current === d3.zoomIdentity) {
            const tx = width / 2;
            const ty = orientation === 'TD' ? 50 : height / 2;
            const offsetX = orientation === 'TD' ? 0 : -100;
            transformRef.current = d3.zoomIdentity.translate(tx + offsetX, ty).scale(0.6);
        }

        // Simulation tick handler - just flag for redraw, don't schedule directly
        simulation.on("tick", () => {
            // Store node positions
            d3Nodes.forEach(n => nodeStateRef.current.set(n.id, { x: n.x, y: n.y, vx: n.vx, vy: n.vy }));
            needsRedrawRef.current = true;

            // Stop simulation when settled to save CPU
            if (simulation.alpha() < 0.02) {
                simulation.stop();
            }
        });

        // Throttled render loop - ~30fps during simulation, responsive to user input
        let running = true;
        let lastRenderTime = 0;
        const MIN_FRAME_TIME = 33; // ~30fps max during simulation

        const renderLoop = () => {
            if (!running) return;

            const now = performance.now();
            const elapsed = now - lastRenderTime;

            // Throttle renders during simulation, but always render if user-triggered (zoom/selection)
            if (needsRedrawRef.current && drawFnRef.current) {
                if (elapsed >= MIN_FRAME_TIME || !simulationRef.current || simulationRef.current.alpha() < 0.02) {
                    drawFnRef.current();
                    needsRedrawRef.current = false;
                    lastRenderTime = now;
                }
            }

            animationFrameRef.current = requestAnimationFrame(renderLoop);
        };
        renderLoop();

        // Initial draw
        needsRedrawRef.current = true;

        return () => {
            running = false;
            if (simulationRef.current) simulationRef.current.stop();
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        };
    }, [nodes, links, dimensions, expandedIds, orientation, visibleNodeIds]);

    // --- EFFECT 2: Visual Updates (Selection, Power Mode) ---
    useEffect(() => {
        // Trigger redraw when visual state changes
        needsRedrawRef.current = true;
    }, [selectedNodeIds, powerVisMode, highlightedPath]);

    // --- Zoom Setup (set up once, stable across simulation changes) ---
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Only create zoom behavior once
        if (zoomBehaviorRef.current) return;

        const canvasSelection = d3.select(canvas);

        // Create zoom behavior - filter to allow zoom only on background
        const zoom = d3.zoom<HTMLCanvasElement, unknown>()
            .scaleExtent([0.1, 5])
            .filter((event: any) => {
                // Always allow wheel zoom
                if (event.type === 'wheel') return true;
                // For mousedown, check if over a node (only on click start, not every move)
                if (event.type === 'mousedown') {
                    const rect = canvas.getBoundingClientRect();
                    const x = event.clientX - rect.left;
                    const y = event.clientY - rect.top;
                    const t = transformRef.current;
                    const tx = (x - t.x) / t.k;
                    const ty = (y - t.y) / t.k;

                    const nodes = d3NodesRef.current;
                    for (let i = nodes.length - 1; i >= 0; i--) {
                        const node = nodes[i];
                        const dx = tx - node.x;
                        const dy = ty - node.y;
                        if (dx * dx + dy * dy < 400) {
                            return false; // On a node, don't start pan
                        }
                    }
                }
                return true;
            })
            .on("zoom", (event: d3.D3ZoomEvent<HTMLCanvasElement, unknown>) => {
                transformRef.current = event.transform;
                needsRedrawRef.current = true;
            });

        zoomBehaviorRef.current = zoom;
        canvasSelection.call(zoom);

        // Apply initial transform if set
        if (transformRef.current && transformRef.current !== d3.zoomIdentity) {
            canvasSelection.call(zoom.transform, transformRef.current);
        }

        return () => {
            canvasSelection.on('.zoom', null);
            zoomBehaviorRef.current = null;
        };
    }, []); // Empty deps - set up once

    // --- Interaction Handlers (separate from zoom) ---
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const canvasSelection = d3.select(canvas);

        // Track click vs drag
        let clickStartTime = 0;
        let clickStartPos = { x: 0, y: 0 };
        let clickedOnBackground = false;
        const CLICK_THRESHOLD = 5;
        const CLICK_TIME_THRESHOLD = 300;

        // Mouse down handler
        canvasSelection.on("mousedown.interaction", (event: MouseEvent) => {
            clickStartTime = Date.now();
            clickStartPos = { x: event.clientX, y: event.clientY };
            clickedOnBackground = false;

            const node = getNodeAtPoint(event.clientX, event.clientY);
            if (node) {
                // Check if clicking on toggle button (use precomputed flags)
                const nodeFlags = nodeFlagsRef.current.get(node.id);
                if (nodeFlags?.hasChildren && isToggleClick(event.clientX, event.clientY, node)) {
                    onToggleExpand(node.id);
                    return;
                }

                // Start drag - zoom filter already prevents zoom on nodes
                dragNodeRef.current = node;
                isDraggingRef.current = false;
                node.fx = node.x;
                node.fy = node.y;
                simulationRef.current?.alphaTarget(0.3).restart();
            } else {
                // Clicked on background (not on a node)
                clickedOnBackground = true;
            }
        });

        // Mouse move handler
        canvasSelection.on("mousemove.interaction", (event: MouseEvent) => {
            if (!dragNodeRef.current) return;

            const dx = event.clientX - clickStartPos.x;
            const dy = event.clientY - clickStartPos.y;
            if (Math.abs(dx) > CLICK_THRESHOLD || Math.abs(dy) > CLICK_THRESHOLD) {
                isDraggingRef.current = true;
            }

            const rect = canvas.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;

            const t = transformRef.current;
            dragNodeRef.current.fx = (x - t.x) / t.k;
            dragNodeRef.current.fy = (y - t.y) / t.k;
        });

        // Mouse up handler
        canvasSelection.on("mouseup.interaction", (event: MouseEvent) => {
            const clickDuration = Date.now() - clickStartTime;
            const dx = event.clientX - clickStartPos.x;
            const dy = event.clientY - clickStartPos.y;
            const isClick = clickDuration < CLICK_TIME_THRESHOLD &&
                Math.abs(dx) < CLICK_THRESHOLD &&
                Math.abs(dy) < CLICK_THRESHOLD;

            if (dragNodeRef.current) {
                if (isClick && !isDraggingRef.current) {
                    if (onNodeClick) onNodeClick(dragNodeRef.current);
                }

                dragNodeRef.current.fx = null;
                dragNodeRef.current.fy = null;
                dragNodeRef.current = null;
                simulationRef.current?.alphaTarget(0);
            } else if (isClick && clickedOnBackground) {
                // Background click - deselect
                if (onBackgroundClick) onBackgroundClick();
            }
            isDraggingRef.current = false;
            clickedOnBackground = false;
        });

        // Mouse leave handler
        canvasSelection.on("mouseleave.interaction", () => {
            if (dragNodeRef.current) {
                dragNodeRef.current.fx = null;
                dragNodeRef.current.fy = null;
                dragNodeRef.current = null;
                simulationRef.current?.alphaTarget(0);
            }
            isDraggingRef.current = false;
            clickedOnBackground = false;
        });

        return () => {
            canvasSelection.on('.interaction', null);
        };
    }, [getNodeAtPoint, isToggleClick, onToggleExpand, onNodeClick, onBackgroundClick]);

    return (
        <div
            ref={containerRef}
            className="w-full h-full bg-[#0d1830] overflow-hidden relative"
        >
            <canvas
                ref={canvasRef}
                className="w-full h-full block"
                style={{ cursor: 'grab' }}
            />
            <div className="absolute bottom-4 right-4 bg-gray-800/80 p-2 rounded text-xs text-gray-400 pointer-events-none">
                {nodes.length} nodes • {links.length} links (Canvas)
            </div>
        </div>
    );
};

export default D3HybridViewer;
